import {
  DEFAULT_LISTING_CAP,
  LISTING_CAP_BY_USER_TYPE,
  LISTING_CAP_BY_GROUP_ID,
} from '../config/r2rListingCaps';

export const OPEN_LISTING_STATES = ['draft', 'pendingApproval', 'published'];
export { DEFAULT_LISTING_CAP };

/**
 * True when the seller has reached or exceeded their listing cap.
 * Grandfathered over-cap sellers (openCount > max) still count as at cap.
 *
 * @param {number} openCount
 * @param {number} max
 * @returns {boolean}
 */
export const isAtListingCap = (openCount, max) => openCount >= max;

/**
 * Remaining open-listing slots for import (and other create-many flows).
 * Grandfathered over-cap sellers have zero remaining.
 *
 * @param {number} openCount
 * @param {number} max
 * @returns {number}
 */
export const remainingImportSlots = (openCount, max) => {
  const open = Number(openCount) || 0;
  const cap = Number(max) || 0;
  return Math.max(0, cap - open);
};

/**
 * UI-ready cap context for gating new listing creation.
 *
 * Callers should obtain `openCount` via:
 *
 * ```js
 * sdk.ownListings
 *   .query({ states: OPEN_LISTING_STATES, page: 1, perPage: 1 })
 *   .then(res => Number(res?.data?.meta?.totalItems ?? 0) || 0);
 * ```
 *
 * (`ManageListingsPage` passes `states` as an array; a comma-separated string also works.)
 *
 * @param {{
 *   userTypeId?: string|null,
 *   xfUserGroupIds?: Array<string|number>,
 *   environment?: string,
 *   openCount?: number,
 *   userTypeLabel?: string|null,
 * }} args
 * @returns {{
 *   openCount: number|undefined,
 *   baseMax: number,
 *   max: number,
 *   isGroupBump: boolean,
 *   isAtCap: boolean,
 *   userTypeLabel: string|null|undefined,
 * }}
 */
export const buildListingCapContext = ({
  userTypeId,
  xfUserGroupIds,
  environment,
  openCount,
  userTypeLabel,
} = {}) => {
  const { baseMax, max, isGroupBump } = resolveListingCap({
    userTypeId,
    xfUserGroupIds,
    environment,
  });
  return {
    openCount,
    baseMax,
    max,
    isGroupBump,
    isAtCap: isAtListingCap(openCount, max),
    userTypeLabel,
  };
};

export const getListingCapBaseForUserType = userTypeId => {
  if (userTypeId && LISTING_CAP_BY_USER_TYPE[userTypeId] != null) {
    return LISTING_CAP_BY_USER_TYPE[userTypeId];
  }
  return DEFAULT_LISTING_CAP;
};

const normalizeEnv = environment => (environment === 'production' ? 'production' : 'test9');

const normalizeGroupIds = ids =>
  (Array.isArray(ids) ? ids : []).map(id => Number(id)).filter(id => Number.isInteger(id));

/**
 * Resolve which R2R environment's group→cap map to use.
 * Prefer the value from `/api/account/refresh-xf-groups`. When that request
 * fails, fall back to `REACT_APP_R2R_ENV` (must match server `R2R_ENV`), then test9.
 *
 * @param {{ r2rEnvironment?: string }|null|undefined} groupRefresh
 * @returns {'production'|'test9'}
 */
export const getClientR2rEnvironment = groupRefresh => {
  const fromRefresh = groupRefresh?.r2rEnvironment;
  if (fromRefresh === 'production' || fromRefresh === 'test9') {
    return fromRefresh;
  }
  const fromEnv = process.env.REACT_APP_R2R_ENV;
  if (fromEnv === 'production' || fromEnv === 'test9') {
    return fromEnv;
  }
  return 'test9';
};

/**
 * @param {{ userTypeId?: string|null, xfUserGroupIds?: Array<string|number>, environment?: string }} args
 * @returns {{ baseMax: number, max: number, isGroupBump: boolean }}
 */
export const resolveListingCap = ({ userTypeId, xfUserGroupIds, environment } = {}) => {
  const baseMax = getListingCapBaseForUserType(userTypeId);
  const map = LISTING_CAP_BY_GROUP_ID[normalizeEnv(environment)] || {};
  const groupMax = normalizeGroupIds(xfUserGroupIds).reduce((acc, id) => {
    const cap = map[id];
    return typeof cap === 'number' && cap > acc ? cap : acc;
  }, 0);
  const max = Math.max(baseMax, groupMax);
  return { baseMax, max, isGroupBump: max > baseMax };
};
